import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Box, Typography, CircularProgress, TableCell, Tooltip } from '@mui/material';
import { colors } from '../../theme';

// api
import BuildingApi from '../../api/building.api';
import TermApi from '../../api/term.api';
import TermListingApi from '../../api/term-listing.api';
import WeekdaysApi from '../../api/weekdays.api';
import { useAppSelector } from '../../hooks/hooks';

// comps
import GlobalButton from '../../components/global/button';
import GlobalInput from '../../components/global/input';
import GlobalDropdown from '../../components/global/dropdown';
import FilterOption from '../../components/global/filterOption';
import ContentContainer from '../../components/layout/contentContainer';
import DataGrid from '../../components/grid/dataGrid';

// icons
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';

// constants
import { BURNABY_CAMPUS_NAME, courseTimeArr } from '../../constants';
import { downloadExcelFile, downloadPDFFile } from '../../helpers/file';
import { MOMENT_DATE_DISPLAY_FORMAT } from '../../constants/date_format';
import RoomApi from '../../api/room.api';
import { isUserTypeCentralTimetabler, isUserTypeFacilities } from '../../helpers/user';

interface ReportProps {
    isFolded: boolean;
}

// Holds the DataGrid's own callbacks. It's a stable ref object, refreshed on every
// renderRows() call, so memoized rows can never invoke a stale onChange.
type GridCallbacksRef = React.MutableRefObject<{ onChange: (name: string, value: any, row: any, dataType?: string) => void }>;

type NoteCellProps = {
    row: any;
    rowKey: string;
    note: string | null | undefined;
    draftsRef: React.MutableRefObject<Map<string, string>>;
    cbRef: GridCallbacksRef;
};

type CourseRowProps = {
    data: any;
    rowKey: string;
    isUserCentralTimetabler: boolean;
    draftsRef: React.MutableRefObject<Map<string, string>>;
    cbRef: GridCallbacksRef;
};

function useDebouncedCallback<T extends (...args: any[]) => void>(cb: T, delayMs: number) {
    const cbRef = useRef(cb);
    cbRef.current = cb;

    const timeoutRef = useRef<number | null>(null);

    const debounced = useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => cbRef.current(...args), delayMs);
        },
        [delayMs],
    );

    useEffect(() => {
        return () => {
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        };
    }, []);

    return debounced;
}

/**
 * Note cell.
 *
 * PERF: this renders as static text until the user clicks it, and only then mounts the real
 * MUI input. Previously every row mounted a ClickAwayListener + Tooltip + TextField, i.e. a
 * document-level listener and ~20 components per row for up to 1000 rows.
 *
 * PERF: must live at module scope. Declaring it inside TermListing produces a new component
 * type on every render, which unmounts and remounts every note cell in the grid.
 */
const NoteCell = memo(function NoteCell({ row, rowKey, note, draftsRef, cbRef }: NoteCellProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [local, setLocal] = useState(() => draftsRef.current.get(rowKey) ?? note ?? '');

    // if server data changes (new load), adopt it unless there's an uncommitted draft
    useEffect(() => {
        const existingDraft = draftsRef.current.get(rowKey);
        setLocal(existingDraft !== undefined ? existingDraft : note ?? '');
    }, [rowKey, note, draftsRef]);

    const commit = useCallback(
        (value: string) => {
            draftsRef.current.set(rowKey, value);
            cbRef.current.onChange('note', value, row, 'default');
        },
        [rowKey, row, draftsRef, cbRef],
    );

    const commitDebounced = useDebouncedCallback(commit, 250);

    const handleChange = useCallback(
        (_name: string, value: string) => {
            setLocal(value); // instant UI update
            draftsRef.current.set(rowKey, value); // keep draft without re-rendering the grid
            commitDebounced(value); // commit less frequently
        },
        [rowKey, commitDebounced, draftsRef],
    );

    if (!isEditing) {
        return (
            <TableCell>
                <NoteDisplay onClick={() => setIsEditing(true)} title={local || ''}>
                    {local ? local : <NotePlaceholder>New Comment</NotePlaceholder>}
                </NoteDisplay>
            </TableCell>
        );
    }

    return (
        // React's onBlur bubbles, so this catches the input losing focus
        <TableCell
            onBlur={() => {
                commit(local); // flush final value immediately
                setIsEditing(false);
            }}
        >
            <GlobalInput autoFocus table={'true'} type="text" name="note" placeholder="New Comment" value={local} onChange={handleChange} />
        </TableCell>
    );
});

/**
 * PERF: the row body is memoized on the row object itself, so a grid-level re-render
 * (sorting, selecting, editing another row) doesn't re-render all ~1000 rows.
 */
const CourseRow = memo(
    function CourseRow({ data, rowKey, isUserCentralTimetabler, draftsRef, cbRef }: CourseRowProps) {
        const { building, course, crn, startDate, endDate, timeIn, timeOut, totalWeeks, enrolled, capacity, sets, partOfTermCode, note, days, room, roomType, roomComments, sectionType } = data;

        const instructors = data.instructors?.split(',');
        let displayInstructors = [];

        if (instructors?.length > 1) {
            displayInstructors = instructors.map((instructor, index) => {
                if (index === instructors.length - 1) return instructor;
                else return instructor + ',';
            });
        }

        const isWeekLong = partOfTermCode === '6' && days === 'MTWRF';

        return (
            <>
                <TableCell sx={{ color: isWeekLong ? colors.blue.main : colors.green, width: '7%' }}>
                    <DetailedTableCell>
                        <Tooltip title={course.title}>
                            <Typography variant="body2" sx={{ lineHeight: 1.15 }}>
                                <span style={{ fontWeight: 500 }}>
                                    {course.subjectCode} {course.number}
                                </span>
                                <InfoIcon />
                            </Typography>
                        </Tooltip>
                        <Typography variant="body2" color={colors.grey.medium}>
                            {sectionType.name}
                        </Typography>
                    </DetailedTableCell>
                </TableCell>
                <TableCell sx={{ width: '2%' }}>{crn}</TableCell>

                <TableCell sx={{ width: '6%', color: isWeekLong && colors.blue.main, fontWeight: isWeekLong && 700 }}>{moment(startDate).format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>
                <TableCell sx={{ width: '6%', color: isWeekLong && colors.blue.main, fontWeight: isWeekLong && 700 }}>{moment(endDate).format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>

                <TableCell sx={{ width: '4%' }}>{timeIn}</TableCell>
                <TableCell sx={{ width: '4%' }}>{timeOut}</TableCell>

                <TableCell sx={{ width: '1%', textAlign: 'center' }}>{totalWeeks}</TableCell>
                <TableCell sx={{ width: '4%', textAlign: 'center' }}>
                    {enrolled}/{capacity}
                </TableCell>
                <TableCell sx={{ width: '8%' }}>
                    <Tooltip
                        title={instructors?.length > 1 ? displayInstructors : ''}
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    fontSize: '0.90rem', // text size
                                },
                            },
                        }}
                    >
                        <Typography variant="body2">
                            {instructors && instructors[0]}
                            {instructors?.length > 1 && ', ...'}
                        </Typography>
                    </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '6%' }}>{sets}</TableCell>
                <TableCell sx={{ width: '1%', textAlign: 'center', textTransform: 'uppercase' }}>{partOfTermCode}</TableCell>
                <TableCell sx={{ width: '3%', textAlign: 'center' }}>{building && room ? building + ' ' + room : ''}</TableCell>
                {isUserCentralTimetabler && <TableCell sx={{ width: '3%', textAlign: 'center' }}>{roomType}</TableCell>}
                {isUserCentralTimetabler && (
                    <TableCell sx={{ width: '12%' }}>
                        <Tooltip
                            title={roomComments?.length > 30 ? roomComments : ''}
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        fontSize: '0.80rem', // text size
                                    },
                                },
                            }}
                        >
                            <Typography variant="body2">
                                {roomComments && roomComments.slice(0, 30)}
                                {roomComments?.length > 30 && '....'}
                            </Typography>
                        </Tooltip>
                    </TableCell>
                )}
                <TableCell sx={{ width: '4%', textAlign: 'center', color: isWeekLong && colors.blue.main, fontWeight: isWeekLong && 700 }}>{days}</TableCell>
                <NoteCell row={data} rowKey={rowKey} note={note} draftsRef={draftsRef} cbRef={cbRef} />
            </>
        );
    },
    // draftsRef and cbRef are stable refs, so only the row data can require a re-render
    (prev, next) => prev.data === next.data && prev.rowKey === next.rowKey && prev.isUserCentralTimetabler === next.isUserCentralTimetabler,
);

const TermListing = (props: any) => {
    const _termApi = new TermApi();
    const _termListingApi = new TermListingApi();
    const _weekDayApi = new WeekdaysApi();
    const _buildingApi = new BuildingApi();
    const _roomApi = new RoomApi();

    const appState = useAppSelector((state) => state.appState);
    const user = useAppSelector((state) => state.user);

    const isUserCentralTimetabler = isUserTypeCentralTimetabler(user, appState.viewType);
    const isUserFacilities = isUserTypeFacilities(user, appState.viewType);
    const isCampusBurnaby = appState.campus?.name?.toUpperCase() === BURNABY_CAMPUS_NAME.toUpperCase();
    const renderBuildingAndRoomTypeSelector = isUserCentralTimetabler || (isUserFacilities && isCampusBurnaby);
    const [isFolded, setIsFolded] = useState(false);
    const [resetPage, setResetPage] = useState(false);

    const defaultState = {
        courseDetails: null,
        labs: [],
        classRooms: [],
        termCampuses: [],
        weekdays: [],
        buildings: [],
        roomTypes: [],
        generateGrid: false,
        isLoading: false,
        filterObject: {
            course: '',
            crn: '',
            instructors: '',
            building: '',
            room: '',
            note: '',
        },
        isClearAll: false,
    };

    const [state, setState] = useState(defaultState);

    const selectedDefaultState = {
        termCampus: null,
        building: null,
        weekday: null,
        type: null,
        roomType: null,
        course: null,
        instructor: null,
        crn: null,
        room: null,
    };

    type SelectedKeys = keyof typeof selectedDefaultState;

    const [selected, setSelected] = useState(selectedDefaultState);

    const [buttonDisabled, setButtonDisabled] = useState(true);

    // Row "memory": one selected row at a time, in memory only (deliberately not persisted).
    const [activeRow, setActiveRow] = useState({});

    const noteDraftsRef = useRef<Map<string, string>>(new Map());

    const getRowKey = useCallback((row: any) => {
        // If CRN is unique in your term listing, this is perfect.
        // If not, change to `${row.crn}-${row.sectionTypeId}-${row.startDate}-${row.timeIn}`
        return String(row.rowId);
    }, []);

    // Refreshed on every renderRows() call below so memoized rows always see the grid's
    // current onChange, never a stale closure over old row data.
    const gridCbRef = useRef({ onChange: (_n: string, _v: any, _r: any, _t?: string) => {} });

    useEffect(() => {
        const loadData = async (campusId: number) => {
            const terms = await _termApi.GetTermsByCampusId(campusId);
            const weekdays = await _weekDayApi.GetAll();
            const buildings = await _buildingApi.GetAllBuildings(campusId);
            let roomTypes = await _roomApi.GetRoomTypes();
            //FIXME: hacky way of adding in All Types
            roomTypes.unshift({ id: 0, name: 'All types' });

            setState((prevState) => ({
                ...prevState,
                termCampuses: terms,
                weekdays,
                buildings,
                roomTypes,
            }));
        };

        if (appState.campus?.id) {
            setSelected(selectedDefaultState);
            setState(defaultState);
            loadData(appState.campus.id).catch((error) => toast.error('Error loading term data'));
        }
    }, [appState.campus]);

    const loadCourseDetails = async () => {
        noteDraftsRef.current.clear();
        setActiveRow({});
        setState({ ...state, generateGrid: true, isLoading: true });

        const roomTypeId = selected.roomType?.id === 0 ? null : selected.roomType?.id ?? null;

        const { labs, classRooms, details } = await _termListingApi.GetTermListing(
            'TermCampus',
            selected.termCampus?.termCampusId,
            selected.course,
            selected.crn,
            selected.weekday?.id,
            selected.type,
            selected.building?.id,
            roomTypeId,
            selected.instructor,
            selected.room,
        );
        const availableClassRooms = classRooms.filter((room: any) => room.isAvailable);
        const availableLabs = labs.filter((lab: any) => lab.isAvailable);
        // Initial sorting
        details.sort((a, b) => {
            if (a.startDate < b.startDate) return -1;
            if (a.startDate > b.startDate) return 1;
            if (a.course?.subjectCode < b.course?.subjectCode) return -1;
            if (a.course?.subjectCode > b.course?.subjectCode) return 1;
        });
        // empty filter object
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            courseDetails: details,
            labs: availableLabs,
            classRooms: availableClassRooms,
            generateGrid: true,
            isLoading: false,
            filterObject,
            isClearAll: true,
        });
    };

    const handleFilterOptionChange = (key: any, value: any) => {
        setSelected({
            ...selected,
            [key]: value,
        });
    };

    const handleFold = () => {
        setIsFolded(!isFolded);
    };

    const handleClearAll = () => {
        setActiveRow({});
        setSelected((prev) => {
            const next: any = { ...prev };

            Object.keys(next).forEach((k) => {
                const v = next[k];
                // if current value is a string (text input), clear to empty string
                // otherwise clear to null
                next[k] = typeof v === 'string' ? '' : null;
            });

            return next;
        });

        setState((prevState) => ({
            ...prevState,
            generateGrid: false,
            labs: [],
            classRooms: [],
            isClearAll: true,
        }));

        setResetPage(true);
    };

    const handleFilter = (name: any, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: value,
            },
        });
        setButtonDisabled(false);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const handleClearFilterObject = () => {
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            isClearAll: true,
            filterObject,
        });
        setResetPage(true);
        setButtonDisabled(true);
    };

    // PERF: memoized so the `data` prop keeps a stable reference across unrelated re-renders.
    // A fresh array every render trips DataGrid's `useEffect(..., [data])`, which resets its
    // internal rowData - an extra full re-render, and it silently drops unsaved note edits.
    const gridData = useMemo(() => {
        if (!state.courseDetails) return [];

        let courseDetails = [...state.courseDetails];
        const { course, crn, instructors, building, room, note } = state.filterObject;

        if (course) {
            courseDetails = courseDetails.filter((item) => {
                const courseInfo = item.course?.subjectCode + ' ' + item.course?.number;
                return courseInfo.toLowerCase().includes(course.toLowerCase());
            });
        }

        if (crn) courseDetails = courseDetails.filter((item) => item.crn.toString().includes(crn));
        if (instructors) courseDetails = courseDetails.filter((item) => item.instructors?.toLowerCase().includes(instructors.toLowerCase()));
        if (building) courseDetails = courseDetails.filter((item) => item.building?.toUpperCase().includes(building.toUpperCase()));
        if (room) courseDetails = courseDetails.filter((item) => item.room?.startsWith(room));
        if (note) courseDetails = courseDetails.filter((item) => item.note?.toLowerCase().includes(note.toLowerCase()));

        return courseDetails;
    }, [state.courseDetails, state.filterObject]);

    const handleUpdateComment = async (dataItem: any): Promise<boolean> => {
        try {
            const { course } = dataItem;
            await _termListingApi.UpdateSectionComment(dataItem);
            toast.success(`Comment for ${course.subjectCode} ${course.number} has been successfully saved.`);
            // await handleGenerateGrid(); //TODO (TS): fix this
            return true;
        } catch (error) {
            toast.error('Error updating comment');
            return false;
        }
    };

    const exportCourseDetailsToExcel = async () => {
        const excelData = await _termListingApi.ExportCourseDetails(selected.termCampus?.termCampusId, selected.weekday?.id, selected.type.value);
        const fileName = `Course_Details_${selected.weekday.shortName}_${selected.type.shortLabel}_${selected.termCampus.termCode.trim()}`;
        downloadExcelFile(excelData, fileName);
    };

    const exportCourseDetailsToPDF = async () => {
        const excelData = await _termListingApi.ExportCourseDetails(selected.termCampus?.termCampusId, selected.weekday?.id, selected.type.value, 'Pdf');
        const fileName = `Course_Details_${selected.weekday.shortName}_${selected.type.shortLabel}_${selected.termCampus.termCode.trim()}`;
        downloadPDFFile(excelData, fileName);
    };

    const getGridColumns = () => {
        const courseGridColumns = [
            {
                id: 'course',
                label: 'Course',
                objectId: 'subjectCode',
            },
            { id: 'crn', label: 'CRN' },
            { id: 'startDate', label: 'Start' },
            { id: 'endDate', label: 'End' },
            { id: 'timeIn', label: 'Time In' },
            { id: 'timeOut', label: 'Time Out' },
            {
                label: 'Weeks',
                sx: { textAlign: 'center' },
                disabled: true,
            },
            {
                id: 'enrolled',
                label: 'Enrol / Cap',
                sx: { textAlign: 'center' },
            },
            { id: 'instructors', label: 'Instructor(s)' },
            { id: 'sets', label: 'Set(s)' },
            {
                label: 'PT',
                sx: { textAlign: 'center' },
                disabled: true,
            },
            {
                id: 'room',
                label: 'Room Info',
                sx: { textAlign: 'center' },
            },
            // conditionally include Room Type for CTT
            ...(isUserCentralTimetabler
                ? [
                      {
                          id: 'roomType',
                          label: 'Room Type',
                          sx: { textAlign: 'center' },
                      },
                  ]
                : []),
            // conditionally include Room Type for CTT
            ...(isUserCentralTimetabler
                ? [
                      {
                          id: 'roomComments',
                          label: 'Room Comment',
                          sx: { textAlign: 'center' },
                      },
                  ]
                : []),
            {
                label: 'Day(s)',
                sx: { textAlign: 'center' },
                disabled: true,
            },
            {
                label: 'Note',
                disabled: true,
            },
        ];

        return courseGridColumns;
    };

    const handleSearchInputsChange = async (name: any, value: any) => {
        setSelected({
            ...selected,
            [name]: value,
        });
    };

    const renderCourseGrid = (data: any, onChange: any) => {
        // keep the memoized rows pointed at the grid's current onChange
        gridCbRef.current.onChange = onChange;

        return <CourseRow data={data} rowKey={getRowKey(data)} isUserCentralTimetabler={isUserCentralTimetabler} draftsRef={noteDraftsRef} cbRef={gridCbRef} />;
    };

    const hasActiveFiltersExcept = (excludedKeys: SelectedKeys[]) => {
        return Object.entries(selected).some(([key, value]) => !excludedKeys.includes(key as SelectedKeys) && value !== null && value !== undefined && value !== '');
    };

    const noFilterSelected = Object.values(selected).every((v) => v === null);
    const hasAnyFilterBesidesCampus = hasActiveFiltersExcept(['termCampus']);

    const handleSearch = async () => {
        const shouldWarn = !hasActiveFiltersExcept(['termCampus', 'building']);

        if (shouldWarn) {
            toast.warning('This search will take longer due to the large dataset.', { autoClose: false });
        }

        if (selected.termCampus && hasAnyFilterBesidesCampus) {
            await loadCourseDetails();
        }
    };

    return (
        <>
            <Box
                sx={{
                    background: 'white',
                    borderRadius: '5px',
                    padding: isFolded ? '10px 15px' : '5px 10px',
                    display: 'flex',
                }}
            >
                {/* Types */}
                <TypeContainer isFolded={isFolded}>
                    {isFolded ? (
                        <>
                            <div>
                                <InactiveLabel>Term</InactiveLabel>
                                <Typography color={colors.blue.dark}>{selected.termCampus ? selected.termCampus.termCode : 'Not selected'}</Typography>
                            </div>
                            {renderBuildingAndRoomTypeSelector && (
                                <>
                                    <div>
                                        <InactiveLabel>Building</InactiveLabel>
                                        <Typography color={colors.blue.dark}>{selected.building ? selected.building.name : 'Not selected'}</Typography>
                                    </div>
                                    <div>
                                        <InactiveLabel>Room Type</InactiveLabel>
                                        <Typography color={colors.blue.dark}>{selected.roomType ? selected.roomType.name : 'Not selected'}</Typography>
                                    </div>
                                </>
                            )}
                            <div>
                                <InactiveLabel>Day</InactiveLabel>
                                {selected.weekday ? <Type>{selected.weekday?.shortName}</Type> : <Typography color={colors.blue.dark}>Not selected</Typography>}
                            </div>
                            <div>
                                <InactiveLabel>Time</InactiveLabel>
                                {selected.type ? <Type>{selected.type?.label}</Type> : <Typography color={colors.blue.dark}>Not selected</Typography>}
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <ActiveLabel>Term</ActiveLabel>
                                <GlobalDropdown
                                    value={selected.termCampus}
                                    data={state.termCampuses}
                                    placeholder="Select a term"
                                    name="termCode"
                                    field="termCampusId"
                                    width="150px"
                                    dataItemKey={'termCampus'}
                                    onChange={(name, value) => handleFilterOptionChange(name, value)}
                                />
                            </div>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px 10px', flexWrap: 'wrap', mt: '5px' }}>
                                {renderBuildingAndRoomTypeSelector && (
                                    <>
                                        <div>
                                            <ActiveLabel>Building</ActiveLabel>
                                            <GlobalDropdown
                                                isSearch={state.buildings.length > 1}
                                                value={selected.building}
                                                data={state.buildings}
                                                placeholder="Select a building"
                                                name="name"
                                                field="id"
                                                width="150px"
                                                dataItemKey={'building'}
                                                onChange={(name, value) => handleFilterOptionChange(name, value)}
                                            />
                                        </div>
                                        <div>
                                            <ActiveLabel>Room Type</ActiveLabel>
                                            <GlobalDropdown
                                                value={selected.roomType}
                                                data={state.roomTypes}
                                                placeholder="Select a room type"
                                                name="name"
                                                field="id"
                                                width="150px"
                                                dataItemKey={'roomType'}
                                                onChange={(name, value) => handleFilterOptionChange(name, value)}
                                            />
                                        </div>
                                    </>
                                )}
                                <div>
                                    <ActiveLabel>Day</ActiveLabel>
                                    <FilterOption
                                        data={state.weekdays}
                                        state={selected?.weekday?.id}
                                        name={'shortName'}
                                        selectedID={'id'}
                                        onOptionClick={(value: any) => {
                                            handleFilterOptionChange('weekday', value);
                                        }}
                                    />
                                </div>
                                <div>
                                    <ActiveLabel>Time</ActiveLabel>
                                    <FilterOption
                                        data={courseTimeArr}
                                        state={selected.type?.value}
                                        name={'label'}
                                        selectedID={'value'}
                                        onOptionClick={(value: any) => {
                                            handleFilterOptionChange('type', value);
                                        }}
                                    />
                                </div>
                                <div>
                                    <ActiveLabel>Course</ActiveLabel>
                                    <GlobalInput placeholder="ex: COMP 1110" type="text" name="course" value={selected.course} onChange={handleSearchInputsChange} />
                                </div>
                                <div>
                                    <ActiveLabel>Instructor</ActiveLabel>
                                    <GlobalInput placeholder="ex: John Smith" type="text" name="instructor" value={selected.instructor} onChange={handleSearchInputsChange} />
                                </div>
                                <div>
                                    <ActiveLabel>CRN</ActiveLabel>
                                    <GlobalInput placeholder="ex: 87110" type="text" name="crn" value={selected.crn} onChange={handleSearchInputsChange} />
                                </div>
                                <div>
                                    <ActiveLabel>Room Number</ActiveLabel>
                                    <GlobalInput placeholder="ex: 370" type="text" name="room" value={selected.room} onChange={handleSearchInputsChange} />
                                </div>
                                {!isFolded && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignSelf: 'self-end',
                                            marginLeft: 'auto',
                                            gap: '10px',
                                        }}
                                    >
                                        <GlobalButton variant="round" text="Go" endIcon={<SearchIcon />} disabled={!hasAnyFilterBesidesCampus} onClick={handleSearch} />

                                        <GlobalButton variant="round" text="Clear All" secondary={'true'} endIcon={<RefreshIcon />} disabled={noFilterSelected} onClick={handleClearAll} />
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </TypeContainer>

                <Box sx={{ display: isFolded ? 'none' : 'flex', flex: '1 1 10%' }}>
                    {/* Available Rooms */}
                    <Box
                        sx={{
                            borderLeft: `1px solid ${colors.border}`,
                            padding: '0 10px',
                            flex: 1,
                        }}
                    >
                        Available Classrooms
                        <Box sx={{ width: '100%', marginTop: '10px' }}>
                            <FilterOption displayInfo data={state.classRooms} name={'number'} selectedID={'id'} displayItem={'configuration'} />
                        </Box>
                    </Box>

                    {/* Available Labs */}
                    <Box
                        sx={{
                            padding: '0 5px 0 0',
                            flex: 1,
                        }}
                    >
                        Available Computer Labs
                        <Box sx={{ width: '100%', marginTop: '10px' }}>
                            <FilterOption displayInfo data={state.labs} name={'number'} selectedID={'id'} displayItem={'configuration'} />
                        </Box>
                    </Box>
                </Box>
                <ArrowContainer onClick={handleFold} isFolded={isFolded}>
                    {isFolded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                </ArrowContainer>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    m: '10px 0',
                }}
            >
                {/* Search Input & Filter/Sort Buttons Container */}
                <div></div>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        '& > :first-of-type': {
                            mr: ' 5px',
                        },
                    }}
                >
                    <span style={{ color: colors.blue.main }}>Downloads: &nbsp;</span>
                    <GlobalButton variant="contained" text="PDF" startIcon={<DownloadIcon />} disabled={!state.generateGrid} onClick={exportCourseDetailsToPDF} />
                    <GlobalButton variant="contained" text="Excel" startIcon={<DownloadIcon />} disabled={!state.generateGrid} onClick={exportCourseDetailsToExcel} />
                </Box>
            </Box>
            {state.generateGrid && !state.isLoading ? (
                <DataGrid
                    isEdit
                    isSave
                    isScrolling
                    resetPage={resetPage}
                    topHeight={isFolded ? 25 : 200}
                    columns={getGridColumns()}
                    data={gridData}
                    renderRows={renderCourseGrid}
                    padding="3px 5px"
                    dataItemKey="rowId"
                    onSaveAsync={(dataItem: any) => handleUpdateComment(dataItem)}
                    isClearSorting={state.isClearAll}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                    highlightItemKey={isUserCentralTimetabler ? 'flagSection' : null}
                    forceHeaderTextOnSingleLine={true}
                    clickable
                    activeCell={activeRow}
                    onRowClick={(item: any) => setActiveRow(item)}
                />
            ) : (
                <ContentContainer
                    height={isFolded ? '200px' : isUserCentralTimetabler ? '500px' : '400px'}
                    padding={'-1px 10px'}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    {state.isLoading ? (
                        <CircularProgress size={100} />
                    ) : (
                        <>
                            <CalendarIcon />
                            <Typography sx={{ mt: '10px', fontSize: '18px' }} color={colors.blue.main}>
                                Please select Term and combination of other filters in the fields above to display course details.
                            </Typography>
                        </>
                    )}
                </ContentContainer>
            )}
        </>
    );
};

export default TermListing;

const TypeContainer = styled.div<ReportProps>`
    margin-right: 20px;
    flex: 2;
    display: flex;
    ${(props) =>
        props.isFolded
            ? `
            & > div {
                margin-bottom: 0;
                display: flex;
                align-items: center;
                p, span {
                    margin-right: 20px;
                }
            }
        `
            : `
            flex-direction: row;
            flex-wrap: wrap;
        `}
`;

const InactiveLabel = styled.div`
    display: inline-block;
    margin-right: 5px;
    padding: 5px 0;
    background-color: ${colors.blue.light};
    color: ${colors.blue.main};
    border-radius: 5px;
    font-size: 10px;
    font-weight: 700;
    width: 55px;
    text-align: center;
`;

const ActiveLabel = styled.div`
    color: #0085e6;
    font-size: 12px;
    font-weight: 700;

    & > span {
        color: #d73948;
    }
`;

const Type = styled.span`
    padding: 3px 7px;
    border-radius: 5px;
    font-size: 14px;
    color: ${colors.blue.main};
`;

const Room = styled.div`
    display: inline-block;
    padding: 2px 5px;
    border: 1px solid #b6b6b6;
    border-radius: 5px;
    margin-right: 10px;
    margin-bottom: 10px;
    cursor: pointer;
`;

const ArrowContainer = styled.span<ReportProps>`
    display: flex;
    align-items: ${(props) => props.isFolded && 'center'};
    color: ${colors.blue.main};
    cursor: pointer;
`;

// Static stand-in for the note input. Mimics the MUI outlined input so the grid looks
// unchanged, but costs one DOM node instead of a Tooltip + ClickAwayListener + TextField.
const NoteDisplay = styled.div`
    display: flex;
    align-items: center;
    min-height: 28px;
    padding: 0 8px;
    border: 1px solid ${colors.border};
    border-radius: 4px;
    background-color: ${colors.white};
    font-size: 12px;
    line-height: 1.2;
    cursor: text;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &:hover {
        border-color: ${colors.blue.main};
    }
`;

const NotePlaceholder = styled.span`
    color: ${colors.grey.medium};
    opacity: 0.65;
`;

const DetailedTableCell = styled.div`
    display: flex;
    flex-direction: column;

    p {
        display: flex;
        align-items: center;

        svg {
            width: 14px;
            height: auto;
            margin-left: 3px;

            path {
                fill: ${colors.grey.medium};
            }
        }
    }
`;
